import React, {useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import AccordionSummary from 'components/atoms/Accordions/AccordionSummary/AccordionSummary';
import Icon from 'components/atoms/Icons/Icon/Icon';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import AccordionDetails from 'components/atoms/Accordions/AccordionDetails/AccordionDetails';
import Typography, {Span} from 'components/atoms/Text/Typography/Typography';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Component from 'components/organisms/Utils/Component/Component';
import Badge from 'components/atoms/Badges/Badge/Badge';
import StyledTableFiltersContentFilter
  from 'components/organisms/TableFilters/TableFiltersContentFilter/TableFiltersContentFilter.styles';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import dom from 'helpers/dom';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import {CloseFilled} from 'assets/icons';

const TableFiltersContentFilter = React.forwardRef((props, ref) => {
  const {
    filter,
    filterGroup,
    tableFilters,
    fieldData,
    onChange,
    onClear,
    ...innerProps
  } = useComponentProps(props, 'TableFiltersContentFilter');

  const innerRef = useRef(null);
  const rangeRef = useRef({});

  const [expanded, setExpanded] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const freeText = Boolean(filter?.type !== constants.formFieldTypes.list);
  const filterType = freeText ? constants.formFieldTypes.list : filter?.type;
  const filterRange = freeText && (
    !constants.formFieldTypes.textTypes.all.includes(filter?.type) || constants.formFieldFormatTypes.numericTypes.includes(filter?.format)
  );

  const renderRange = useCallback((option, props) => {
    const value = (props.label ?? '').toString().replace(/^\[|\]$/g, '');

    rangeRef.current.from = value.split(':')?.[0] ?? null;
    rangeRef.current.to = value.split(':')?.[1] ?? null;

    const handleChange = (field, value) => {
      const formatChange = (from, to) => {
        if (utils.isEmpty(from) && utils.isEmpty(to)) {
          return '';
        } else {
          const dates = !(utils.isInt(from) || utils.isInt(to));
          if (utils.isEmpty(from)) {
            return `[:${dates ? utils.dayjs(to).format('YYYY-MM-DD') : to}]`;
          } else if (utils.isEmpty(to)) {
            return `[${dates ? utils.dayjs(from).format('YYYY-MM-DD') : from}:]`;
          } else {
            return `[${dates ? utils.dayjs(from).format('YYYY-MM-DD') : from}:${dates ? utils.dayjs(to).format('YYYY-MM-DD') : to}]`;
          }
        }
      }

      let change;
      if (field.name === 'from') {
        change = formatChange(value, rangeRef.current.to);
      } else {
        change = formatChange(rangeRef.current.from, value);
      }

      props.onChange(option, true)({
        target: {
          name: props.name,
          value: change
        }
      }, true);
    }

    const fields = [
      {
        ...filter,
        name: 'from',
        inlineLabel: filter.type === constants.formFieldTypes.date ? 'From date' : 'From value',
        placeholder: filter.type === constants.formFieldTypes.date ? 'From date' : 'From value',
        type: filter.type,
        validation: filter.validation,
        conversion: filter.conversion,
        initial: rangeRef.current.from ?? '',
        FormFieldProps: {
          ...filter.FormFieldProps,
          onFocus: !props.checked ? props.onChange(option, false, true) : null,
          size: props.size === 'medium' ? 'smaller' : (props.size === 'small' ? 'smallest' : 'tiny'),
          variant: 'inlineLabel',
          autoFocus: false,
          hiddenLabel: true
        }
      },
      {
        name: 'between',
        initial: 'to',
        type: constants.formFieldTypes.text,
        readOnly: true,
        FormFieldProps: {
          size: props.size === 'medium' ? 'smaller' : (props.size === 'small' ? 'smallest' : 'tiny'),
          hiddenLabel: true
        }
      },
      {
        ...filter,
        name: 'to',
        label: 'to',
        inlineLabel: filter.type === constants.formFieldTypes.date ? 'To date' : 'To value',
        placeholder: filter.type === constants.formFieldTypes.date ? 'To date' : 'To value',
        type: filter.type,
        validation: filter.validation,
        conversion: filter.conversion,
        initial: rangeRef.current.to ?? '',
        FormFieldProps: {
          ...filter.FormFieldProps,
          onFocus: !props.checked ? props.onChange(option, false, true) : null,
          size: props.size === 'medium' ? 'smaller' : (props.size === 'small' ? 'smallest' : 'tiny'),
          variant: 'inlineLabel',
          autoFocus: false,
          hiddenLabel: true
        }
      }
    ];

    return <InlineForm className="TableFiltersContentFilter-range"
                       fields={fields}
                       fieldData={fieldData}
                       onChange={handleChange}/>
  }, [filter, fieldData]);

  const filterFields = useMemo(() => {
    const initial = (filterType !== constants.formFieldTypes.list) ? filter.initial :
      utils.toArray(filter.initial, true).map((v) => {
        return (filter.conversion === constants.formFieldConversionTypes.label) ? {
          label: v.toString().startsWith('-') ? v.toString().slice(1) : v,
          indeterminate: v.toString().startsWith('-')
        } : {
          value: v.toString().startsWith('-') ? v.toString().slice(1) : v,
          indeterminate: v.toString().startsWith('-')
        }
      });

    return [{
      ...filter,
      initial,
      type: filterType,
      validation: (filterType === constants.formFieldTypes.list) ? constants.formFieldValidationTypes.list :
        filter.validation ?? constants.formFieldValidationTypes.text,
      conversion: (filterType === constants.formFieldTypes.list) ? constants.formFieldConversionTypes.none :
        filter.conversion,
      debounce: constants.debounce.input,
      defaultOptions: filterType !== constants.formFieldTypes.list ? null : (
        freeText ? [{
          label: 'No data',
          value: '0'
        }] : filter.defaultOptions
      ),
      options: filterType !== constants.formFieldTypes.list ? null : (
        freeText ? [] : filter.options
      ),
      FormFieldProps: {
        hiddenHelperText: true,
        ...(filterType === constants.formFieldTypes.list ? {
          multiple: true,
          indeterminate: true,
          InputProps: {
            indeterminateIcon: <Icon icon={CloseFilled} color="error"/>
          },
          ListProps: {
            catchFocus: false
          },
          ...(freeText ? {
            freeTextOption: true,
            FreeTextOptionProps: {
              placeholder: filter.placeholder ?? filter.label
            },
            ...(filterRange ? {
              renderOptionLabel: (option, props) => {
                if (option.freeTextOption) {
                  return renderRange(option, props);
                } else {
                  return props.label;
                }
              }
            } : {})
          } : {})
        } : {}),
        ...filter.FormFieldProps
      }
    }]
  }, [filter, filterType, filterRange, freeText, renderRange]);

  const onChangeEvent = useEffectEvent(onChange);
  const handleChange = useCallback((field, value) => {
    if (filterType === constants.formFieldTypes.list) {
      if (utils.toArray(value, true).find((v) => v?.freeTextOption && !(v?.label?.length > 0))) {
        return;
      }

      if (filter.conversion === constants.formFieldConversionTypes.label) {
        value = utils.toArray(value, true).map((v) => `${v?.indeterminate ? '-' : ''}${v?.labelValue ?? v?.label ?? v}`);
      } else {
        value = utils.toArray(value, true)
          .map((v) => `${v?.indeterminate ? '-' : ''}${v?.freeTextOption ? (v?.label ?? v) : (v?.value ?? v)}`);
      }
    }

    onChangeEvent?.(filter, value);
  }, [filter, filterType, onChangeEvent]);

  const handleExpand = (e, expanded) => {
    setExpanded(expanded);
  }

  const handleEntered = () => {
    utils.retry(() => {
      if (innerRef.current) {
        dom.scrollIntoView(innerRef.current);
      }
    }, 5).then();
  }

  useLayoutEffect(() => {
    setExpanded(false);
  }, [tableFilters.state.searchValue]);

  const renderFilterBadge = (filter) => {
    if (filter.activeCount > 0) {
      return <Badge badgeContent={<Tooltip title={'Clear'}>
                                    <Span onClick={(e) => {
                                            onClear?.(filter);
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}>
                                      {filter.activeCount}
                                    </Span>
                                  </Tooltip>}
                    clickable={true} />
    } else {
      return null;
    }
  }

  const renderFilterTitle = (filter) => {
    return <Typography className="TableFiltersContentFilter-title" variant="subtitle2">
      <Markdown>{filter.label}</Markdown>
    </Typography>
  }

  const renderFilter = (filter) => {
    const renderedFilter = <InlineForm className="TableFiltersContentFilter-filter"
                                       fields={filterFields}
                                       fieldData={fieldData}
                                       onChange={handleChange}/>

    if (filter.Filter) {
      return <Component Original={filter.Filter}
                        className="TableFiltersContentFilter-filter"
                        tableFilters={tableFilters}
                        filterGroup={filterGroup}
                        filter={filter}
                        onFilterChange={handleChange}
                        renderedFilter={renderedFilter} />
    } else {
      return renderedFilter;
    }
  }

  return <StyledTableFiltersContentFilter ref={innerRef} {...innerProps}
                                          expanded={expanded}
                                          onChange={handleExpand}
                                          elevation={0}
                                          density="dense"
                                          square={true}
                                          disableGutters
                                          TransitionProps={{
                                            mountOnEnter: true,
                                            unmountOnExit: true,
                                            onEntered: handleEntered
                                          }}>
    <AccordionSummary badge={renderFilterBadge(filter)}
                      expandIcon={<Icon icon={ArrowDropDown} />}>
      {renderFilterTitle(filter)}
    </AccordionSummary>
    <AccordionDetails>
      {renderFilter(filter)}
    </AccordionDetails>
  </StyledTableFiltersContentFilter>
});

TableFiltersContentFilter.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  filter: PropTypes.object,
  filterGroup: PropTypes.object,
  tableFilters: PropTypes.object,
  fieldData: PropTypes.object,
  onChange: PropTypes.func,
  onClear: PropTypes.func
};

export default TableFiltersContentFilter;
